import type { AttributeValue, Attributes } from '../logger-types';

export const REDACTED_PLACEHOLDER = '[REDACTED]';

/**
 * Normalized PII (Personally Identifiable Information) key patterns — substring matched against the **normalized**
 * form of the attribute key (lowercased, non-alphanumeric chars stripped).
 *
 * Adding a new pattern:
 *   1) Add a lowercase, alphanumeric-only string to this list.
 *   2) That's it. The same entry catches every case/separator variant:
 *      `Email`, `userEmail`, `user_email`, `user-email`, `user.email`,
 *      `USER_EMAIL` all match the `'email'` pattern.
 *
 * Substring matching means broad coverage at the cost of occasional false
 * positives (e.g. a key like `tokenizer` matches `'token'`). For logging
 * that's acceptable — better to over-redact than to leak.
 */
export const PII_KEY_PATTERNS: ReadonlyArray<string> = [
  'password',
  'token',
  'authorization',
  'cookie',
  'email',
  'phone',
  'firstname',
  'lastname',
  'givenname',
  'familyname',
  'surname',
];

const JWT_PATTERN = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/;

function normalizeKey(key: string): string {
  return key.toLowerCase().replace(/[^a-z0-9]/g, '');
}

function isPiiKey(key: string): boolean {
  const normalized = normalizeKey(key);
  return PII_KEY_PATTERNS.some((pattern) => normalized.includes(pattern));
}

function looksLikeJwt(value: AttributeValue): boolean {
  return typeof value === 'string' && JWT_PATTERN.test(value);
}

export function scrubPii(attributes: Attributes): Attributes {
  const scrubbed: Attributes = {};
  for (const [key, value] of Object.entries(attributes)) {
    if (isPiiKey(key) || looksLikeJwt(value)) {
      scrubbed[key] = REDACTED_PLACEHOLDER;
    } else {
      scrubbed[key] = value;
    }
  }
  return scrubbed;
}
